import React, { useContext } from 'react';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import useDossierArticle, {
    type DossierArticle,
    type DossierArticleWithInvoice,
} from './useDossierArticle';
import useDossierProcesses from './useDossierProcesses';
import useDossierLegalAnnoucements from './useDossierLegalAnnoucements';
import DocumentPreviewContext from '../providers/DocumentPreviewContext';
import { VALIDATOR_TYPE_BODACC, VALIDATOR_TYPE_LEGAL_ANNOUCEMENT } from '../constants/validators';

const useDossierProcessesActionsAndRender = () => {
    const { getArticlesWithInvoices, deleteArticle } = useDossierArticle();
    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const {
        getArticleLastValidProcess,
        getProcessesByArticle,
        updateValidatorProcess,
        findProcessByArticle,
    } = useDossierProcesses();
    const { getLegalAnnoucement, getLegalAnnoucementAppUrl, getLegalAnnoucementFileContent } =
        useDossierLegalAnnoucements();
    const { setDocumentPreview } = useContext(DocumentPreviewContext);
    const { enqueueSnackbar } = useSnackbar();
    const { palette } = useTheme();
    const handleDeleteArticle = (
        articleId: string,
        setProcessing: (value: React.SetStateAction<boolean>) => void,
        actionOnDone: () => void,
    ) => {
        setProcessing(true);

        deleteArticle(articleId)
            .then(() => {
                setProcessing(false);
                modalActions.close();
            })
            .catch((err) =>
                apiErrorMessageSnackbar(
                    err,
                    'Une erreur est survenue lors de la suppression de la démarche',
                ),
            )
            .finally(() => {
                setProcessing(false);

                actionOnDone();
            });
    };

    /**
     *
     * Load legal anouncement from article validator processes
     *
     * @param dossier DossierOpenSearch
     * @param categoryId number
     * @param validatorTypeIds  number[]
     * @returns Promise
     */
    const loadLegalAnnoucements = async (
        dossier: DossierOpenSearch,
        categoryId: number,
        validatorTypeIds: number[],
    ) => {
        const articles = await getArticlesWithInvoices(dossier.id as number, categoryId);

        const identifiers = articles
            .map((article) => {
                const process = findProcessByArticle(dossier, validatorTypeIds, article.id);

                if (!process) {
                    return undefined;
                }

                return process.identifier;
            })
            .filter((identifier) => identifier !== undefined && identifier !== null);

        const promises = identifiers.map((identifier) =>
            getLegalAnnoucement(identifier as unknown as number),
        );

        return await Promise.all(promises);
    };

    const getLegalAnnoucementByArticle = (
        articleId: number,
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        legalAnnoucements: Array<LegalAnnoucement>,
    ): LegalAnnoucement | undefined => {
        const process = getArticleLastValidProcess(dossier, validatorTypeId, articleId);

        if (!process || !process.identifier) {
            return undefined;
        }

        return legalAnnoucements.find((item) => Number(item.id) === Number(process.identifier));
    };

    const handleShowLegalAnnoucement = async (
        articleId: number,
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        legalAnnoucements: Array<LegalAnnoucement>,
    ) => {
        const legalAnnoucement = getLegalAnnoucementByArticle(
            articleId,
            dossier,
            validatorTypeId,
            legalAnnoucements,
        );
        const baseUrl = getLegalAnnoucementAppUrl();

        if (baseUrl && legalAnnoucement) {
            window.open(`${baseUrl}/annonce-detail-${legalAnnoucement.lien}.html`, '_blank');
        }
    };

    const handlePublishLegalAnnoucement = async (
        article: DossierArticle,
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        onSuccess: () => void,
        onDone: () => void,
    ) => {
        try {
            const processes = getProcessesByArticle(dossier, validatorTypeId, article.id);
            await updateValidatorProcess(processes[0].id as number, 'validate');
            enqueueSnackbar('Votre annonce a bien été publiée');

            onSuccess();
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Une erreur est survenue durant la publication de votre annonce', {
                variant: 'error',
            });
        } finally {
            onDone();
        }
    };

    const handleShowAttestation = (legalAnnoucement: LegalAnnoucement): Promise<void> =>
        new Promise((resolve, reject) => {
            getLegalAnnoucementFileContent(legalAnnoucement.id)
                .then((fileContent: Blob) => {
                    setDocumentPreview({
                        fileName: legalAnnoucement.numero,
                        fileType: 'pdf',
                        content: fileContent,
                    });

                    resolve(undefined);
                })
                .catch(reject);
        });

    const getItemIcons = (
        article: DossierArticleWithInvoice,
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        legalAnnoucements: Array<LegalAnnoucement>,
    ) => {
        const legalAnnoucement = getLegalAnnoucementByArticle(
            article.id,
            dossier,
            validatorTypeId,
            legalAnnoucements,
        );

        if (!legalAnnoucement) {
            return [];
        }

        const icons = [];
        if (validatorTypeId === VALIDATOR_TYPE_BODACC) {
            if (legalAnnoucement.justifBodacc) {
                icons.push({
                    icon: 'file-circle-check' as IconName,
                    title: 'Voir le justificatif',
                    target: legalAnnoucement.justifBodacc,
                });
            }
        } else if (legalAnnoucement.justif) {
            icons.push({
                icon: 'file-circle-check' as IconName,
                title: 'Voir le justificatif',
                target: legalAnnoucement.justif,
            });
        }

        if (validatorTypeId === VALIDATOR_TYPE_LEGAL_ANNOUCEMENT) {
            if (legalAnnoucement.idDocumentAttestation) {
                icons.push({
                    icon: 'file' as IconName,
                    title: "Voir l'attestation",
                    execOnClick: () => handleShowAttestation(legalAnnoucement),
                });
            }
            if (article.hasInvoices) {
                icons.push({
                    icon: 'file-invoice' as IconName,
                    title: `Facture - ${article.invoiceNumber as string}`,
                });
            }
        }

        return icons;
    };

    const actionShowLegalAnouncement = (
        articleId: number,
        dossier: DossierOpenSearch,
        validatorTypeId: number,
        legalAnnoucements: LegalAnnoucement[],
    ) => ({
        label: "Voir l'annonce",
        icon: 'eye' as IconName,
        onClick: () =>
            handleShowLegalAnnoucement(articleId, dossier, validatorTypeId, legalAnnoucements),
    });

    const actionRemoveLinkedLegalAnouncement = (
        actionOnValidation: (article: DossierArticle) => void,
        actionOnCancellation: () => void,
    ) => ({
        label: "Supprimer le rattachement à l'annonce",
        icon: 'trash' as IconName,
        onClick: (article: DossierArticle) => {
            modalActions.call(
                <ConfirmationModal
                    color={palette.error}
                    messages={{
                        question: 'Confirmer cette action ?',
                        detail: {
                            message:
                                'Êtes vous sur de vouloir supprimer le rattachement à cette annonce ?',
                            severity: 'error',
                        },
                        validation: 'Supprimer',
                    }}
                    actionOnValidation={() => actionOnValidation(article)}
                    actionOnCancellation={actionOnCancellation}
                />,
            );
        },
    });

    const actionUpdateValidatorStatus = (onClick: () => void) => ({
        label: 'Modifier le statut valideur',
        icon: 'arrow-progress' as IconName,
        onClick,
    });

    const actionDeleteArticlePresta = (onvalidation: () => void, onCancellation: () => void) => ({
        label: 'Supprimer la démarche',
        icon: 'trash' as IconName,
        onClick: () => {
            modalActions.call(
                <ConfirmationModal
                    color={palette.error}
                    messages={{
                        question: 'Confirmer cette action ?',
                        detail: {
                            message: 'Êtes vous sur de vouloir supprimer la démarche ?',
                            severity: 'error',
                        },
                        validation: 'Supprimer',
                    }}
                    actionOnValidation={onvalidation}
                    actionOnCancellation={onCancellation}
                />,
            );
        },
    });

    const actionAttachLegalAnouncement = (actionOnClick: () => void) => ({
        label: 'Raccrocher une annonce légale',
        icon: 'link' as IconName,
        onClick: actionOnClick,
    });

    const actionPublishLegalAnouncement = (
        actionOnValidation: () => void,
        actionOnCancellation: () => void,
    ) => ({
        label: "Publier l'annonce",
        icon: 'cloud-arrow-up' as IconName,
        onClick: () => {
            modalActions.call(
                <ConfirmationModal
                    color={palette.primary}
                    messages={{
                        question: 'Confirmer cette action ?',
                        detail: {
                            message: 'Êtes vous sur de vouloir publier cette annonce ?',
                            severity: 'info',
                        },
                        validation: 'Publier',
                    }}
                    actionOnValidation={actionOnValidation}
                    actionOnCancellation={actionOnCancellation}
                />,
            );
        },
    });

    return {
        handleDeleteArticle,
        loadLegalAnnoucements,
        getItemIcons,
        getLegalAnnoucementByArticle,
        handleShowLegalAnnoucement,
        handlePublishLegalAnnoucement,
        actionRemoveLinkedLegalAnouncement,
        actionUpdateValidatorStatus,
        actionShowLegalAnouncement,
        actionDeleteArticlePresta,
        actionAttachLegalAnouncement,
        actionPublishLegalAnouncement,
    };
};

export default useDossierProcessesActionsAndRender;
